import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Alert,
  LinearProgress,
  IconButton,
  InputAdornment,
  Divider,
  Badge,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Tabs,
  Tab,
  Pagination,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Send,
  Psychology,
  DirectionsCar,
  Build,
  Schedule,
  LocalOffer,
  AttachMoney,
  Person,
  Phone,
  Email,
  WhatsApp,
  Sms,
  Refresh,
  Settings,
  History,
  Assessment,
  SmartToy,
  CheckCircle,
  Warning,
  Info,
  Timer,
  CloudUpload,
  Download,
} from '@mui/icons-material';
import { simulatorService } from '../services/simulatorService';
import ContactSupport from '@mui/icons-material/ContactSupport';
import { customerService } from '../services/customerService';
import { scenarioService, Scenario as ApiScenario } from '../services/scenarioService';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface Customer {
  id: string;
  name: string;
  phone: string;
  email: string;
  vehicleOwned?: string;
  lastService?: string;
  purchaseDate?: string;
  preferredContact: 'whatsapp' | 'sms' | 'email';
}

interface Scenario {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  systemPrompt: string;
  suggestedQuestions: string[];
  expectedOutcomes: string[];
  kpis: {
    name: string;
    target: number;
  }[];
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  confidence?: number;
  intent?: string;
}

interface SimulationSession {
  id: string;
  customerId: string;
  customerName: string;
  scenarioId: string;
  scenarioName: string;
  channel: 'whatsapp' | 'sms';
  startTime: Date;
  endTime?: Date;
  messages: Message[];
  status: 'active' | 'completed' | 'terminated';
  metrics: {
    responseTime: number[];
    confidence: number[];
    customerSatisfaction?: number;
    goalAchieved?: boolean;
  };
}

// Helper function to convert API scenarios to Simulator scenarios
const convertApiScenarioToSimulatorScenario = (apiScenario: ApiScenario): Scenario => {
  // Define scenario-specific content
  const scenarioContent: Record<string, { icon: React.ReactNode, questions: string[], outcomes: string[], kpis: { name: string, target: number }[] }> = {
    'car-trade-in': {
      icon: <DirectionsCar />,
      questions: [
        "What's my current vehicle worth?",
        "What new models do you recommend for me?",
        "Can I get better financing rates?",
        "Do you have any electric vehicles?",
        "What's the monthly payment difference?"
      ],
      outcomes: [
        "Trade-in value assessment provided",
        "New vehicle recommendations made",
        "Financing options discussed",
        "Test drive scheduled",
        "Lead qualified for sales"
      ],
      kpis: [
        { name: "Interest Level", target: 80 },
        { name: "Appointment Rate", target: 60 },
        { name: "Response Quality", target: 90 }
      ]
    },
    'car-service-reminder': {
      icon: <Build />,
      questions: [
        "What service does my car need?",
        "How much will it cost?",
        "When can I schedule an appointment?",
        "Do you have any service specials?",
        "Can you pick up my vehicle?"
      ],
      outcomes: [
        "Service needs identified",
        "Cost estimate provided",
        "Appointment scheduled",
        "Customer concerns addressed",
        "Service history reviewed"
      ],
      kpis: [
        { name: "Booking Rate", target: 70 },
        { name: "Customer Satisfaction", target: 95 },
        { name: "Response Accuracy", target: 90 }
      ]
    },
    'promo-deals': {
      icon: <LocalOffer />,
      questions: [
        "What special offers are available?",
        "Can I combine multiple promotions?",
        "How long is this offer valid?",
        "What's the best deal for my budget?",
        "Do you have financing specials?"
      ],
      outcomes: [
        "Promotion details explained",
        "Customer eligibility verified",
        "Urgency created",
        "Interest level assessed",
        "Next steps defined"
      ],
      kpis: [
        { name: "Engagement Rate", target: 85 },
        { name: "Conversion Intent", target: 65 },
        { name: "Information Clarity", target: 95 }
      ]
    }
  };

  const content = scenarioContent[apiScenario.id] || {
    icon: <Psychology />,
    questions: [],
    outcomes: [],
    kpis: []
  };

  return {
    id: apiScenario.id,
    name: apiScenario.name,
    description: apiScenario.description || '',
    icon: content.icon,
    systemPrompt: apiScenario.prompt_template,
    suggestedQuestions: content.questions,
    expectedOutcomes: content.outcomes,
    kpis: content.kpis
  };
};

const Simulator: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<'whatsapp' | 'sms'>('whatsapp');
  const [currentSession, setCurrentSession] = useState<SimulationSession | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [simulationHistory, setSimulationHistory] = useState<SimulationSession[]>([]);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [showScenarioDetails, setShowScenarioDetails] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loadingScenarios, setLoadingScenarios] = useState(false);

  // Real SMS state
  const [smsCustomerName, setSmsCustomerName] = useState('');
  const [smsPhoneNumber, setSmsPhoneNumber] = useState('');
  const [smsVehicleInfo, setSmsVehicleInfo] = useState('');
  const [smsScenario, setSmsScenario] = useState('service_reminder');
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsResult, setSmsResult] = useState<any>(null);

  // Pagination for customer selection
  const [currentPage, setCurrentPage] = useState(1);
  const customersPerPage = 5;

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // useEffect(() => {
  //   scrollToBottom();
  // }, [messages]);

  // Fetch customers on component mount
  useEffect(() => {
    const fetchCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const fetchedCustomers = await customerService.getCustomers(0, 100);
        // Transform API response to match component's Customer interface
        const transformedCustomers = fetchedCustomers.map(c => ({
          id: c.id,
          name: c.name,
          phone: c.phone,
          email: c.email || '',
          vehicleOwned: c.vehicle_make && c.vehicle_model
            ? `${c.vehicle_make} ${c.vehicle_model}`
            : 'No vehicle info',
          lastService: c.last_service_date || 'Never',
          purchaseDate: c.created_at || '',
          preferredContact: 'whatsapp' as const
        }));
        setCustomers(transformedCustomers);
      } catch (error) {
        console.error('Failed to fetch customers:', error);
      } finally {
        setLoadingCustomers(false);
      }
    };
    fetchCustomers();
  }, []);

  // Fetch scenarios on component mount
  useEffect(() => {
    const fetchScenarios = async () => {
      setLoadingScenarios(true);
      try {
        const apiScenarios = await scenarioService.getScenarios();
        const simulatorScenarios = apiScenarios.map(convertApiScenarioToSimulatorScenario);
        setScenarios(simulatorScenarios);
      } catch (error) {
        console.error('Failed to fetch scenarios:', error);
      } finally {
        setLoadingScenarios(false);
      }
    };
    fetchScenarios();
  }, []);

  const startSimulation = async () => {
    if (!selectedCustomer || !selectedScenario) {
      return;
    }

    try {
      // Track scenario usage
      await scenarioService.trackUsage(selectedScenario.id);

      // Call the real API to start the simulation using the scenario prompt from the database
      const response = await simulatorService.startSimulation({
        customer_id: selectedCustomer.id,
        scenario_id: selectedScenario.id,
        scenario_name: selectedScenario.name,
        channel: selectedChannel,
        system_prompt: selectedScenario.systemPrompt, // This now comes from the database
        customer_context: {
          name: selectedCustomer.name,
          vehicle_owned: selectedCustomer.vehicleOwned,
          purchase_date: selectedCustomer.purchaseDate,
          last_service: selectedCustomer.lastService,
          phone: selectedCustomer.phone,
          email: selectedCustomer.email,
        },
      });

      const session: SimulationSession = {
        id: response.session_id,
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.name,
        scenarioId: selectedScenario.id,
        scenarioName: selectedScenario.name,
        channel: selectedChannel,
        startTime: new Date(response.created_at),
        messages: [],
        status: response.status === 'active' ? 'active' : 'terminated',
        metrics: {
          responseTime: [],
          confidence: [],
        },
      };

      // Add system message
      const systemMessage: Message = {
        id: '1',
        role: 'system',
        content: `Simulation started. Scenario: ${selectedScenario.name}. You are now chatting as ${selectedCustomer.name} via ${selectedChannel === 'whatsapp' ? 'WhatsApp' : 'SMS'}.`,
        timestamp: new Date(),
      };

      // Add initial proactive outreach message from API or generate it
      const greetingMessage: Message = {
        id: '2',
        role: 'assistant',
        content: response.initial_message,
        timestamp: new Date(),
        confidence: 95,
        intent: 'proactive_outreach',
      };

      setCurrentSession(session);
      setMessages([systemMessage, greetingMessage]);
    } catch (error) {
      console.error('Failed to start simulation:', error);
      // Fallback to local simulation if API fails
      const session: SimulationSession = {
        id: Date.now().toString(),
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.name,
        scenarioId: selectedScenario.id,
        scenarioName: selectedScenario.name,
        channel: selectedChannel,
        startTime: new Date(),
        messages: [],
        status: 'active',
        metrics: {
          responseTime: [],
          confidence: [],
        },
      };

      const systemMessage: Message = {
        id: '1',
        role: 'system',
        content: `Simulation started (offline mode). Scenario: ${selectedScenario.name}.`,
        timestamp: new Date(),
      };

      setCurrentSession(session);
      setMessages([systemMessage]);
    }
  };

  const stopSimulation = async () => {
    if (currentSession) {
      try {
        // Call the API to end the simulation
        const response = await simulatorService.endSimulation(currentSession.id);

        const endedSession = {
          ...currentSession,
          endTime: new Date(),
          status: 'completed' as const,
          messages: messages,
          metrics: {
            ...currentSession.metrics,
            customerSatisfaction: response.metrics.average_confidence,
            goalAchieved: response.metrics.goals_achieved.length > 0,
          },
        };
        setSimulationHistory([...simulationHistory, endedSession]);
      } catch (error) {
        console.error('Failed to end simulation:', error);
        // Fallback to local handling if API fails
        const endedSession = {
          ...currentSession,
          endTime: new Date(),
          status: 'completed' as const,
          messages: messages,
        };
        setSimulationHistory([...simulationHistory, endedSession]);
      }

      setCurrentSession(null);
      setMessages([]);
      setInputMessage('');
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !currentSession) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      // Call the real API to send the message
      const response = await simulatorService.sendMessage(currentSession.id, {
        message: inputMessage,
        role: 'user',
      });

      const aiResponse: Message = {
        id: response.message_id,
        role: 'assistant',
        content: response.content,
        timestamp: new Date(response.timestamp),
        confidence: response.confidence,
        intent: response.intent,
      };

      setMessages(prev => [...prev, aiResponse]);

      // Check if handoff was detected and lead was created
      if (response.metadata?.handoff_data && response.metadata?.lead_created) {
        // Show notification that lead was created
        const handoffMessage: Message = {
          id: Date.now().toString() + '-handoff',
          role: 'system',
          content: `🎯 Lead created! Customer ${selectedCustomer?.name} has been added to the dashboard. Reason: ${response.metadata.handoff_data.reason}. Priority: ${response.metadata.handoff_data.urgency}. Sales agents can now view and claim this lead.`,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, handoffMessage]);

        // Optional: Show a success notification or redirect to leads page
        console.log('Lead created in dashboard:', response.metadata.handoff_data);
      }

      // Update metrics if available
      if (response.metadata) {
        setCurrentSession(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            metrics: {
              ...prev.metrics,
              responseTime: [...prev.metrics.responseTime, response.metadata?.response_time_ms || 0],
              confidence: [...prev.metrics.confidence, response.confidence || 0],
            },
          };
        });
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Fallback to mock response if API fails
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: generateAIResponse(inputMessage, selectedScenario!),
        timestamp: new Date(),
        confidence: 85 + Math.random() * 10,
        intent: detectIntent(inputMessage),
      };
      setMessages(prev => [...prev, aiResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  const generateAIResponse = (userInput: string, scenario: Scenario): string => {
    // This is a mock response generator. In production, this would use OpenAI API
    const input = userInput.toLowerCase();

    if (scenario.id === 'trade-in') {
      if (input.includes('worth') || input.includes('value')) {
        return "I'd be happy to help you get an accurate trade-in value for your vehicle. Based on your 2021 BMW 3 Series with its current mileage and condition, I can provide you with a preliminary estimate. Could you tell me the current mileage and overall condition of your vehicle?";
      }
      if (input.includes('electric') || input.includes('ev')) {
        return "Great question! We have several electric vehicles available including the BMW iX, i4, and the new i7. These offer excellent performance with zero emissions. Would you like to know more about any specific model?";
      }
      return "That's a great point. Let me help you with that. Could you provide more details about what you're looking for?";
    }

    if (scenario.id === 'service') {
      if (input.includes('cost') || input.includes('price')) {
        return "For a full service on your vehicle, the cost typically ranges from €350-€450 depending on what's needed. This includes oil change, filter replacements, and a comprehensive inspection. Would you like me to schedule an appointment for a detailed quote?";
      }
      if (input.includes('appointment') || input.includes('book')) {
        return "I can help you schedule that. We have availability this week on Thursday at 9 AM or Friday at 2 PM. We also have several slots next week. Which would work better for you?";
      }
      return "I understand your concern. Let me help you schedule the appropriate service for your vehicle.";
    }

    return "I understand. Let me help you with that. Could you provide more specific details about what you need?";
  };

  const detectIntent = (message: string): string => {
    const input = message.toLowerCase();
    if (input.includes('trade') || input.includes('sell')) return 'trade_inquiry';
    if (input.includes('service') || input.includes('maintenance')) return 'service_request';
    if (input.includes('price') || input.includes('cost')) return 'price_inquiry';
    if (input.includes('appointment') || input.includes('schedule')) return 'scheduling';
    if (input.includes('finance') || input.includes('loan')) return 'financing';
    return 'general_inquiry';
  };

  const renderSetupTab = () => (
    <Grid container spacing={3}>
      {/* Customer Selection */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
              Select Customer
            </Typography>
            {loadingCustomers ? (
              <LinearProgress />
            ) : customers.length === 0 ? (
              <Alert severity="info">
                No customers available. Please upload customers via CSV first.
              </Alert>
            ) : (
            <>
              <List>
                {(() => {
                  // Calculate pagination
                  const startIndex = (currentPage - 1) * customersPerPage;
                  const endIndex = startIndex + customersPerPage;
                  const currentCustomers = customers.slice(startIndex, endIndex);
                  const totalPages = Math.ceil(customers.length / customersPerPage);

                  return (
                    <>
                      {currentCustomers.map((customer) => (
                        <ListItem
                          key={customer.id}
                          button
                          selected={selectedCustomer?.id === customer.id}
                          onClick={() => setSelectedCustomer(customer)}
                          sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            mb: 1,
                            '&.Mui-selected': {
                              bgcolor: '#E3F2FD',
                              borderColor: '#1a237e',
                            },
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: '#1a237e' }}>
                              {customer.name.charAt(0)}
                            </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={customer.name}
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          {customer.phone} • {customer.email}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Vehicle: {customer.vehicleOwned}
                        </Typography>
                        <Chip
                          size="small"
                          icon={customer.preferredContact === 'whatsapp' ? <WhatsApp /> : <Sms />}
                          label={customer.preferredContact}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                          />
                        </ListItem>
                      ))}

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
                          <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(event, page) => setCurrentPage(page)}
                            size="small"
                            variant="outlined"
                            showFirstButton
                            showLastButton
                          />
                        </Box>
                      )}

                      {/* Customer count info */}
                      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                        Showing {currentCustomers.length} of {customers.length} customers
                      </Typography>
                    </>
                  );
                })()}
              </List>
              <Button
              fullWidth
              variant="outlined"
              startIcon={<CloudUpload />}
              sx={{ mt: 2 }}
              onClick={() => setShowCustomerDialog(true)}
            >
              Load More Customers
            </Button>
            </>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Scenario Selection */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <Psychology sx={{ mr: 1, verticalAlign: 'middle' }} />
              Select Scenario
            </Typography>
            {loadingScenarios ? (
              <Alert severity="info">
                Loading scenarios...
              </Alert>
            ) : scenarios.length === 0 ? (
              <Alert severity="warning">
                No scenarios available. Please configure scenarios in Settings → Simulator Prompts.
              </Alert>
            ) : (
            <RadioGroup
              value={selectedScenario?.id || ''}
              onChange={(e) => {
                const scenario = scenarios.find(s => s.id === e.target.value);
                setSelectedScenario(scenario || null);
              }}
            >
              {scenarios.map((scenario) => (
                <Card
                  key={scenario.id}
                  sx={{
                    mb: 2,
                    border: selectedScenario?.id === scenario.id ? '2px solid #1a237e' : '1px solid #e0e0e0',
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedScenario(scenario)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Radio value={scenario.id} />
                      <Box sx={{ ml: 1, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {scenario.icon}
                          <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600 }}>
                            {scenario.name}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {scenario.description}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                          <Chip label={`${scenario.kpis.length} KPIs`} size="small" />
                          <Chip label={`${scenario.expectedOutcomes.length} Outcomes`} size="small" />
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowScenarioDetails(true);
                        }}
                      >
                        <Info />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </RadioGroup>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Channel & Settings */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <Settings sx={{ mr: 1, verticalAlign: 'middle' }} />
              Simulation Settings
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Communication Channel</InputLabel>
                  <Select
                    value={selectedChannel}
                    onChange={(e) => setSelectedChannel(e.target.value as 'whatsapp' | 'sms')}
                    label="Communication Channel"
                  >
                    <MenuItem value="whatsapp">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <WhatsApp sx={{ mr: 1 }} />
                        WhatsApp
                      </Box>
                    </MenuItem>
                    <MenuItem value="sms">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Sms sx={{ mr: 1 }} />
                        SMS
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <Alert severity="info">
                  <Typography variant="body2">
                    The AI assistant will respond according to the selected scenario and maintain context
                    throughout the conversation. You will play the role of {selectedCustomer?.name || 'the customer'}.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Start Simulation Button */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<PlayArrow />}
            onClick={() => {
              startSimulation();
              setActiveTab(1);
            }}
            disabled={!selectedCustomer || !selectedScenario}
            sx={{
              bgcolor: '#4CAF50',
              '&:hover': { bgcolor: '#43A047' },
              px: 4,
              py: 1.5,
            }}
          >
            Start Simulation
          </Button>
        </Box>
      </Grid>
    </Grid>
  );

  const renderSimulationTab = () => (
    <Grid container spacing={2} sx={{ height: 'calc(50vh - 125px)' }}>
      {/* Conversation Area */}
      <Grid item xs={12} md={8}>
        <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f5f5f5' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: '#1a237e', mr: 2 }}>
                  <SmartToy />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Doxa Motors AI Assistant
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={currentSession?.scenarioName || 'No Scenario'}
                      size="small"
                      color="primary"
                    />
                    <Chip
                      icon={selectedChannel === 'whatsapp' ? <WhatsApp /> : <Sms />}
                      label={selectedChannel}
                      size="small"
                      variant="outlined"
                    />
                    {currentSession && (
                      <Chip
                        icon={<Timer />}
                        label="Active"
                        size="small"
                        color="success"
                      />
                    )}
                  </Box>
                </Box>
              </Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Stop />}
                onClick={() => {
                  stopSimulation();
                  setActiveTab(0);
                }}
                disabled={!currentSession}
              >
                End Simulation
              </Button>
            </Box>
          </Box>

          {/* Messages */}
          <Box sx={{ height: '800px', overflow: 'auto', p: 2 }}>
            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 2,
                }}
              >
                {message.role === 'system' ? (
                  <Alert severity="info" sx={{ width: '100%' }}>
                    {message.content}
                  </Alert>
                ) : (
                  <Box
                    sx={{
                      maxWidth: '70%',
                      bgcolor: message.role === 'user' ? '#1a237e' : '#f5f5f5',
                      color: message.role === 'user' ? 'white' : 'text.primary',
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    {message.role === 'assistant' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <SmartToy sx={{ fontSize: 16, mr: 1 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          AI Assistant
                        </Typography>
                        {message.confidence && (
                          <Chip
                            label={`${message.confidence.toFixed(0)}% confidence`}
                            size="small"
                            sx={{ ml: 1, height: 20 }}
                          />
                        )}
                      </Box>
                    )}
                    {message.role === 'user' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Person sx={{ fontSize: 16, mr: 1 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {selectedCustomer?.name}
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="body2">{message.content}</Typography>
                    {message.intent && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                        Intent: {message.intent}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            ))}
            {isTyping && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 2, p: 2 }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    AI is typing...
                  </Typography>
                </Box>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
            <TextField
              fullWidth
              placeholder={`Type as ${selectedCustomer?.name || 'customer'}...`}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              disabled={!currentSession}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={sendMessage}
                      disabled={!currentSession || !inputMessage.trim()}
                      sx={{ color: '#1a237e' }}
                    >
                      <Send />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Paper>
      </Grid>

      {/* Sidebar Info */}
      <Grid item xs={12} md={4}>
        <Paper sx={{ height: '100%', p: 2, overflow: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            Simulation Info
          </Typography>

          {/* Customer Info */}
          <Card sx={{ mb: 2, bgcolor: '#f5f5f5' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Person sx={{ mr: 1, color: '#1a237e' }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  Customer Profile
                </Typography>
              </Box>
              {selectedCustomer && (
                <Box>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Personal Information
                    </Typography>
                    <Typography variant="body2">
                      <strong>{selectedCustomer.name}</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Phone sx={{ fontSize: 14, mr: 0.5 }} />
                      <Typography variant="body2">{selectedCustomer.phone}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Email sx={{ fontSize: 14, mr: 0.5 }} />
                      <Typography variant="body2">{selectedCustomer.email}</Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Vehicle Details
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <DirectionsCar sx={{ fontSize: 14, mr: 0.5 }} />
                      <Typography variant="body2">
                        <strong>{selectedCustomer.vehicleOwned || 'No vehicle info'}</strong>
                      </Typography>
                    </Box>
                    {selectedCustomer.purchaseDate && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Purchased: {new Date(selectedCustomer.purchaseDate).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Service History
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Build sx={{ fontSize: 14, mr: 0.5 }} />
                      <Typography variant="body2">
                        Last Service: {selectedCustomer.lastService || 'Never'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Next Due: {(() => {
                        if (!selectedCustomer.lastService || selectedCustomer.lastService === 'Never') {
                          return 'Schedule soon';
                        }
                        const lastServiceDate = new Date(selectedCustomer.lastService);
                        lastServiceDate.setMonth(lastServiceDate.getMonth() + 6);
                        return lastServiceDate.toLocaleDateString();
                      })()}
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Chip
                      size="small"
                      icon={selectedCustomer.preferredContact === 'whatsapp' ? <WhatsApp /> : <Sms />}
                      label={`Prefers ${selectedCustomer.preferredContact}`}
                      sx={{ bgcolor: '#E8F5E9' }}
                    />
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Scenario Hints */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Suggested Questions
              </Typography>
              <List dense>
                {selectedScenario?.suggestedQuestions.map((question, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => setInputMessage(question)}
                    sx={{
                      bgcolor: '#E3F2FD',
                      mb: 1,
                      borderRadius: 1,
                      '&:hover': { bgcolor: '#BBDEFB' },
                    }}
                  >
                    <ListItemText primary={question} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Expected Outcomes */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Expected Outcomes
              </Typography>
              {selectedScenario?.expectedOutcomes.map((outcome, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle sx={{ fontSize: 16, mr: 1, color: '#4CAF50' }} />
                  <Typography variant="body2">{outcome}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>

          {/* KPIs */}
          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Performance Metrics
              </Typography>
              {selectedScenario?.kpis.map((kpi, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">{kpi.name}</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {Math.floor(Math.random() * 20 + 75)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.floor(Math.random() * 20 + 75)}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Paper>
      </Grid>
    </Grid>
  );

  const handleStartRealSMS = async () => {
    if (!smsCustomerName.trim() || !smsPhoneNumber.trim()) {
      setSmsResult({ success: false, error: 'Please provide customer name and phone number' });
      return;
    }

    setSmsLoading(true);
    setSmsResult(null);

    try {
      // Get token from auth store (checks both localStorage and sessionStorage)
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        setSmsResult({ success: false, error: 'Not authenticated. Please log in again.' });
        setSmsLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/v1/simulator/start-real-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customer_name: smsCustomerName,
          phone_number: smsPhoneNumber,
          vehicle_info: smsVehicleInfo || undefined,
          scenario_id: smsScenario
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSmsResult({ success: true, data });
        // Clear form on success
        setSmsCustomerName('');
        setSmsPhoneNumber('');
        setSmsVehicleInfo('');
      } else {
        setSmsResult({ success: false, error: data.detail || 'Failed to send SMS' });
      }
    } catch (error) {
      setSmsResult({ success: false, error: 'Network error. Please check if the backend is running.' });
    } finally {
      setSmsLoading(false);
    }
  };

  const renderRealSMSTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <Sms sx={{ mr: 1, verticalAlign: 'middle' }} />
              Start Real SMS Conversation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Send a real SMS message via SendMode to start a conversation with a customer
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Customer Name"
                value={smsCustomerName}
                onChange={(e) => setSmsCustomerName(e.target.value)}
                fullWidth
                required
                disabled={smsLoading}
              />

              <TextField
                label="Phone Number"
                value={smsPhoneNumber}
                onChange={(e) => setSmsPhoneNumber(e.target.value)}
                fullWidth
                required
                disabled={smsLoading}
                placeholder="+27821234567"
                helperText="Include country code (e.g., +27 for South Africa)"
              />

              <TextField
                label="Vehicle Information"
                value={smsVehicleInfo}
                onChange={(e) => setSmsVehicleInfo(e.target.value)}
                fullWidth
                disabled={smsLoading}
                placeholder="e.g., Toyota Corolla 2020"
                helperText="Optional: Vehicle details for personalized message"
              />

              <FormControl fullWidth>
                <InputLabel>Scenario</InputLabel>
                <Select
                  value={smsScenario}
                  onChange={(e) => setSmsScenario(e.target.value)}
                  disabled={smsLoading}
                  label="Scenario"
                >
                  <MenuItem value="service_reminder">Service Reminder</MenuItem>
                  <MenuItem value="trade_in">Trade-In Offer</MenuItem>
                  <MenuItem value="test_drive">Test Drive Invitation</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={handleStartRealSMS}
                disabled={smsLoading || !smsCustomerName.trim() || !smsPhoneNumber.trim()}
                startIcon={smsLoading ? <CircularProgress size={20} /> : <Send />}
                fullWidth
                size="large"
              >
                {smsLoading ? 'Sending SMS...' : 'Send SMS'}
              </Button>

              {smsResult && (
                <Alert severity={smsResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
                  {smsResult.success ? (
                    <>
                      <strong>SMS Sent Successfully!</strong>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Conversation ID: {smsResult.data.conversation_id}
                      </Typography>
                      <Typography variant="body2">
                        Message: {smsResult.data.message_sent}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {smsResult.data.note}
                      </Typography>
                    </>
                  ) : (
                    <strong>{smsResult.error}</strong>
                  )}
                </Alert>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              About Real SMS Mode
            </Typography>
            <Typography variant="body2" paragraph>
              This feature allows you to start real SMS conversations with customers using the SendMode API.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>How it works:</strong>
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>
                <Typography variant="body2">
                  Creates or finds the customer in the database
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Generates a personalized greeting based on the selected scenario
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Sends the SMS via SendMode API
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Stores the conversation and message in the database
                </Typography>
              </li>
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Note:</strong> Real SMS messages will be sent via SendMode API. Standard SMS charges apply based on destination and message length.
            </Alert>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderHistoryTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <History sx={{ mr: 1, verticalAlign: 'middle' }} />
              Simulation History
            </Typography>
            {simulationHistory.length === 0 ? (
              <Alert severity="info">
                No simulation history yet. Complete some simulations to see them here.
              </Alert>
            ) : (
              <List>
                {simulationHistory.map((session) => (
                  <ListItem key={session.id} sx={{ border: '1px solid #e0e0e0', mb: 1, borderRadius: 1 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: session.status === 'completed' ? '#4CAF50' : '#FF9800' }}>
                        {session.status === 'completed' ? <CheckCircle /> : <Warning />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">
                            {session.customerName} - {session.scenarioName}
                          </Typography>
                          <Chip label={session.channel} size="small" />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2">
                            Started: {session.startTime.toLocaleString()}
                          </Typography>
                          <Typography variant="body2">
                            Messages: {session.messages.length} | Duration: {
                              session.endTime
                                ? Math.round((session.endTime.getTime() - session.startTime.getTime()) / 60000)
                                : 0
                            } minutes
                          </Typography>
                        </Box>
                      }
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Assessment />}
                    >
                      View Details
                    </Button>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          AI Conversation Simulator
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Chip
            icon={<SmartToy />}
            label="OpenAI Powered"
            sx={{ bgcolor: '#E8F5E9', color: '#4CAF50' }}
          />
          <Button
            variant="outlined"
            startIcon={<Download />}
            sx={{ borderColor: '#1a237e', color: '#1a237e' }}
          >
            Export Sessions
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Setup" icon={<Settings />} iconPosition="start" />
          <Tab label="Simulation" icon={<PlayArrow />} iconPosition="start" disabled={!currentSession} />
          <Tab label="Real SMS" icon={<Sms />} iconPosition="start" />
          <Tab label="History" icon={<History />} iconPosition="start" />
        </Tabs>
      </Paper>

      <Box sx={{ mt: 3 }}>
        {activeTab === 0 && renderSetupTab()}
        {activeTab === 1 && renderSimulationTab()}
        {activeTab === 2 && renderRealSMSTab()}
        {activeTab === 3 && renderHistoryTab()}
      </Box>

      {/* Customer Dialog */}
      <Dialog open={showCustomerDialog} onClose={() => setShowCustomerDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Load Customer Database</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            In production, this would load customers from your CRM database.
          </Alert>
          <Typography variant="body2">
            Additional customers can be imported from your existing database or CRM system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCustomerDialog(false)}>Close</Button>
          <Button
            variant="contained"
            sx={{ bgcolor: '#1a237e' }}
            onClick={() => {
              setShowCustomerDialog(false);
              navigate('/upload');
            }}
          >
            Import Customers
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scenario Details Dialog */}
      <Dialog
        open={showScenarioDetails}
        onClose={() => setShowScenarioDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Scenario Configuration</DialogTitle>
        <DialogContent>
          {selectedScenario && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedScenario.name}
              </Typography>
              <Typography variant="body2" paragraph>
                {selectedScenario.description}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                System Prompt:
              </Typography>
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mb: 2 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {selectedScenario.systemPrompt}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowScenarioDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Simulator;